import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import PinLegendType from "@mapmycustomers/shared/enum/PinLegendType";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import useMapView from "@app/component/view/utils/useMapView";
import { getAllColorLegends, getAllShapeLegends, isDeleting } from "@app/store/pinLegends";
import { deletePinLegend } from "@app/store/pinLegends/actions";
import { RootState } from "@app/store/rootReducer";
import { getDefaultColorPinLegend, getDefaultShapePinLegend } from "@app/util/pinLegend";

import styles from "./DeleteButton.module.scss";

interface Props {
  colorLegends: PinLegend[];
  deleting?: boolean;
  onDelete?: (payload: { callback?: () => void; pinLegendId: PinLegend["id"] }) => void;
  onDeleted?: () => void;
  pinLegend: PinLegend;
  shapeLegends: PinLegend[];
}

const DeleteButton: React.FC<Props> = ({
  colorLegends,
  deleting,
  onDelete,
  onDeleted,
  pinLegend,
  shapeLegends,
}) => {
  const intl = useIntl();

  const {
    updateViewStateAndFetch,
    viewState: { colorKey, shapeKey },
  } = useMapView();

  const entityType = pinLegend.entity;
  const type = pinLegend.type;
  const currentPinLegendId = (type === PinLegendType.COLOR ? colorKey : shapeKey)?.[entityType];

  const handleDelete = useCallback(() => {
    onDelete?.({
      callback: () => {
        if (pinLegend.id === currentPinLegendId) {
          // if deleted currently selected pin legend, let's switch to the default ones
          updateViewStateAndFetch({
            request: {
              [type === PinLegendType.COLOR ? "colorKey" : "shapeKey"]: {
                [entityType]: (type === PinLegendType.COLOR
                  ? getDefaultColorPinLegend
                  : getDefaultShapePinLegend)(
                  type === PinLegendType.COLOR ? colorLegends : shapeLegends,
                  entityType
                )?.id,
              },
            },
          });
        }
        onDeleted?.();
      },
      pinLegendId: pinLegend.id,
    });
  }, [
    colorLegends,
    currentPinLegendId,
    entityType,
    onDelete,
    onDeleted,
    pinLegend.id,
    shapeLegends,
    type,
    updateViewStateAndFetch,
  ]);

  return (
    <Popconfirm
      cancelText={intl.formatMessage({
        id: "map.colorization.createEditSidebar.delete.confirmation.cancel",
        defaultMessage: "Cancel",
        description: "No button in a Delete Pin Legend confirmation",
      })}
      description={intl.formatMessage({
        id: "map.colorization.createEditSidebar.delete.confirmation.message",
        defaultMessage:
          "Any users this legend is shared with will no longer be able to access this legend.",
        description: "Delete button confirmation in a Delete Pin Legend",
      })}
      okButtonProps={{ disabled: deleting, loading: deleting }}
      okText={intl.formatMessage({
        id: "map.colorization.createEditSidebar.delete.confirmation.ok",
        defaultMessage: "Delete",
        description: "Yes button in a Delete Pin Legend confirmation",
      })}
      onConfirm={handleDelete}
      title={intl.formatMessage({
        id: "map.colorization.createEditSidebar.delete.confirmation.title",
        defaultMessage: "Are you sure?",
        description: "Delete button confirmation title in a Delete Pin Legend",
      })}
    >
      <Button
        block
        className={styles.container}
        danger
        disabled={deleting}
        loading={deleting}
        type="default"
      >
        {intl.formatMessage(
          {
            id: "map.colorization.createEditSidebar.delete",
            defaultMessage: "{icon} Delete Legend",
            description: "Delete button in the Pin Legend edit pane",
          },
          {
            icon: <FontAwesomeIcon icon={faTrash} />,
          }
        )}
      </Button>
    </Popconfirm>
  );
};

const mapStateToProps = (state: RootState) => ({
  colorLegends: getAllColorLegends(state),
  deleting: isDeleting(state),
  shapeLegends: getAllShapeLegends(state),
});

const mapDispatchToProps = {
  onDelete: deletePinLegend.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DeleteButton);
